import { useLayoutEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useColonyAuth } from "@/contexts/ColonyAuthContext";
import { api } from "@/lib/api";
import { colonyApi } from "@/lib/colonyApi";

// Only interaction timestamps are retained in memory: no keystrokes, values,
// coordinates, screen contents, URLs or off-site browsing are transmitted.
const trackedSurface = (path) => /^\/managementusa\/admin(?:\/|$)/.test(path)
  ? "management" : path === "/worker" ? "worker" : /^\/colonies(?:\/tickets(?:\/|$)|\/?$)/.test(path) ? "colony" : null;

export const ManagementUsageTracker = () => {
  const { pathname } = useLocation();
  const { user } = useAuth();
  const { user: colonyUser } = useColonyAuth();
  const surface = trackedSurface(pathname);
  const actor = surface === "colony" ? colonyUser : user;
  const eligible = actor && !actor.is_demo && surface && (surface === "colony" || ["mgmt", "worker"].includes(actor.role));

  useLayoutEffect(() => {
    if (!eligible) return;
    let lastInput = performance.now(), lastPing = -Infinity, pending = false, stopped = false;
    const sessionId = crypto.randomUUID();
    const controller = new AbortController();
    const client = surface === "colony" ? colonyApi : api;
    const endpoint = surface === "colony" ? "/colony/usage/ping" : "/mgmt/usage/ping";
    const ping = async () => {
      const now = performance.now();
      if (stopped || controller.signal.aborted || pending || trackedSurface(window.location.pathname) !== surface || document.visibilityState !== "visible" || !document.hasFocus() || now - lastInput > 120000 || now - lastPing < 30000) return;
      lastPing = now;
      pending = true;
      try { await client.post(endpoint, { session_id: sessionId, surface }, { timeout: 10000, signal: controller.signal }); }
      catch (error) { if ([401, 403].includes(error.response?.status)) stopped = true; }
      finally { pending = false; }
    };
    const interact = () => { lastInput = performance.now(); ping(); };
    const focused = () => { if (document.visibilityState === "visible") interact(); };
    const leaving = () => { stopped = true; controller.abort(); };
    const events = ["pointerdown", "pointermove", "keydown", "scroll", "touchstart"];
    events.forEach(event => window.addEventListener(event, interact, { passive: true }));
    window.addEventListener("focus", focused);
    document.addEventListener("visibilitychange", focused);
    window.addEventListener("pagehide", leaving);
    window.addEventListener("beforeunload", leaving);
    const timer = window.setInterval(ping, 15000);
    ping();
    return () => {
      leaving();
      clearInterval(timer);
      events.forEach(event => window.removeEventListener(event, interact));
      window.removeEventListener("focus", focused);
      document.removeEventListener("visibilitychange", focused);
      window.removeEventListener("pagehide", leaving);
      window.removeEventListener("beforeunload", leaving);
    };
  }, [eligible, actor?.id, surface]);
  return null;
};